import swal from "sweetalert2";
import { getField, updateField } from "vuex-map-fields";
import { nanoid } from "nanoid";
import _ from "lodash";
import httpClient from "@/mixins/httpClient";

import router from "@/router";

const getDefaultState = () => {
  return {
    creating: false,
    package: null,
    status: null,
    advertiser: null,
    defaultImages: [],
    editDataSet: false,
    editLimitedDataSet: false,
    logoImageDefault: null,
    contactImageDefault: null,
    campaignVideoDefault: null,
    postcode: null,
    id: null,
    query: null,
    form: {
      advertiser: "",
      businessAddress: {
        flat: "",
        postcode: "",
        state: "",
        streetName: "",
        streetNumber: "",
        suburb: "",
        description: "",
        coordinates: { lat: null, lng: null },
        googleMapsPlaceEid: "",
      },
      businessAddressDisplay: true,
      name: "",
      package: "",
      digitalHeadline: "",
      imageQuantity: null,
      longDescription: "",
      shortDescription: "",
      searchHeadline1: "",
      searchHeadline2: "",
      agentEmail: "",
      agentName: "",
      agentPhone: "",
      agentPhoneDisplay: true,
      campaignImages: [],
      campaignVideo: "",
      callToActionBackgroundColor: "",
      callToActionTextColor: "",
      contactImage: "",
      logoImage: "",
      callToActionType: "",
      creativeStatus: "",
      insertionDays: [],
      landingPage: false,
      startDate: "",
      externalUrl: "",
      extraLocationsTargeting: "",
      extraPostcodesTargeting: "",
      callToActionUrl: "",
      content: "",
    },
  };
};

export const state = getDefaultState();

export const getters = {
  getField,
};

export const mutations = {
  updateField,
  RESET_STATE(state) {
    Object.assign(state, getDefaultState());
  },
  SET_CREATING(state, value) {
    state.creating = value;
  },
  SET_ID(state, id) {
    state.id = id;
  },
  IMAGE_ORDER(state, value) {
    value.forEach((item, index) => {
      item.position = index + 1;
    });

    state.form.campaignImages = value;
  },
  UPDATE_IMAGE_QUANTITY(state, value) {
    if (state.form.campaignImages.length) {
      if (state.form.campaignImages.length > value) {
        state.form.campaignImages.splice(2, value);

        if (state.defaultImages.length) {
          state.defaultImages.splice(2, value);
        }
      } else {
        const imageArr = [...state.form.campaignImages];

        for (
          let index = 0;
          index < value - state.form.campaignImages.length;
          index++
        ) {
          imageArr.push({
            image: "",
            position: imageArr.length + 1,
            id: nanoid(),
          });
        }

        state.form.campaignImages = imageArr;

        if (state.defaultImages.length) {
          const defaultImageArr = [...state.defaultImages];

          for (
            let index = 0;
            index < value - state.defaultImages.length;
            index++
          ) {
            defaultImageArr.push({ url: "", filename: "" });
          }

          state.defaultImages = defaultImageArr;
        }
      }
    } else {
      const imageArr = [];

      for (let index = 0; index < value; index++) {
        imageArr.push({ image: "", position: index + 1, id: nanoid() });
      }

      state.form.campaignImages = imageArr;
    }
  },
  HANDLE_IMAGE_DELETE(state, index) {
    state.form.campaignImages[index].image = "";
  },
  HANDLE_IMAGE_UPLOAD(state, { id, position }) {
    const imageObject = state.form.campaignImages.find(
      (image) => image.position === position
    );

    if (imageObject) {
      imageObject.image = id;
    }
  },
  HANDLE_VIDEO_DELETE(state) {
    state.form.campaignVideo = "";
  },
  HANDLE_VIDEO_UPLOAD(state, { id }) {
    state.form.campaignVideo = id;
  },
  handleAddressData(state, { address_components, geometry, place_id }) {
    const addressData = {};
    address_components.forEach((component) => {
      if (component.types[0] === "administrative_area_level_1") {
        addressData[component.types[0]] = component.short_name;
      } else {
        addressData[component.types[0]] = component.long_name;
      }
    });

    state.form.businessAddress.coordinates = geometry.location.toJSON();
    state.form.businessAddress.googleMapsPlaceEid = place_id;

    state.form.businessAddress.streetNumber = addressData.street_number;
    state.form.businessAddress.streetName = addressData.route;
    state.form.businessAddress.flat = addressData.subpremise;
    state.form.businessAddress.suburb = addressData.locality;
    state.form.businessAddress.postcode = addressData.postal_code;
    state.form.businessAddress.state = addressData.administrative_area_level_1;
  },
  handleAddressEdit: _.debounce((state) => {
    const { businessAddress } = state.form;

    httpClient.methods
      .apiGetPublic("https://maps.googleapis.com/maps/api/geocode/json", {
        address: `${businessAddress.streetNumber} ${businessAddress.streetName}, ${businessAddress.suburb} ${businessAddress.state} ${businessAddress.postcode}`,
        sensor: false,
        key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
        components: `country:AU`,
      })
      .then((data) => {
        const { status, results } = data;

        if (status === "OK") {
          state.form.businessAddress.coordinates.lat =
            results[0].geometry.location.lat;
          state.form.businessAddress.coordinates.lng =
            results[0].geometry.location.lng;
          state.form.businessAddress.googleMapsPlaceEid = results[0].place_id;
        }
      });
  }, 1000),
  handleCallToActionBackgroundColorUpdate(state) {
    if (state.package) {
      if (
        state.package.type === "Digital" ||
        state.package.type === "Print & digital"
      ) {
        state.form.callToActionBackgroundColor =
          state.advertiser.backgroundColor;
      } else {
        state.form.callToActionBackgroundColor = null;
      }
    }
  },
  FORMAT_DATA(state, data) {
    Object.assign(state.form, data);

    state.form.advertiser = data.advertiser.id;

    state.advertiser = data.advertiser;

    state.businessAddress = data.businessAddress ? data.businessAddress : null;

    state.postcode = data.package.masthead.postcode;

    state.form.name = data.name;
    state.form.package = data.package.id;

    state.package = data.package;

    state.form.logoImage = data.logoImage ? data.logoImage.id : "";
    state.form.contactImage = data.contactImage ? data.contactImage.id : "";

    state.logoImageDefault = data.logoImage
      ? { url: data.logoImage.url, filename: data.logoImage.filename }
      : null;
    state.contactImageDefault = data.contactImage
      ? { url: data.contactImage.url, filename: data.contactImage.filename }
      : null;

    state.insertionDates = data.insertionDates;

    if (data.campaignImages && data.campaignImages.length) {
      const imageArray = [];

      const defaultImages = [];
      data.campaignImages.forEach(({ image, position }) => {
        const id = nanoid();
        imageArray.push({ image: image.id, position, id });
        defaultImages.push({ url: image.url, filename: image.filename, id });
      });
      state.form.campaignImages = imageArray;

      state.defaultImages = defaultImages;
    }

    state.form.campaignVideo = data.campaignVideo ? data.campaignVideo.id : "";

    state.campaignVideoDefault = data.campaignVideo
      ? { url: data.campaignVideo.url, filename: data.campaignVideo.filename }
      : null;

    if (data.callToActionType) {
      state.form.callToActionType = data.callToActionType.name;
    }

    if (state.form.businessAddress) {
      delete state.form.businessAddress.id;
    }

    delete state.form.id;
    delete state.form.createdAt;
    delete state.form.updatedAt;
    delete state.form.deletedAt;
    delete state.form.status;
    delete state.form.endDate;
    delete state.form.insertionDates;
    delete state.form.landingPageUrl;
    delete state.form.slug;

    state.editDataSet = true;
    state.editLimitedDataSet =
      data.status === "Upcoming" || data.status === "In Progress";
  },
};

export const actions = {
  async create({ state, commit }, { bookNow, preview, status }) {
    commit("SET_CREATING", true);

    try {
      // If all campaignImages don't have images set then remove empty campaignImages objects
      if (
        state.form.campaignImages.filter(
          (campaignImage) => campaignImage.image === ""
        ).length
      ) {
        state.form.campaignImages = null;
      }

      let postData = {};

      if (status === "Incomplete" || status === "Draft") {
        postData = { ...state.form, status };
      } else {
        postData = { ...state.form };
      }

      if (state.package.type === "Video") {
        delete postData.logoImage;
        delete postData.contactImage;
      }

      await httpClient.methods
        .apiPost(`${process.env.VUE_APP_API_URL}/campaigns`, postData)
        .then((id) => {
          commit("SET_ID", id);
        });

      if (bookNow) {
        router.push({ path: `/campaigns/${state.id}/payment` });
        commit("RESET_STATE");
        commit("SET_CREATING", false);
      } else if (preview) {
        commit("SET_CREATING", false);
        return;
      } else {
        const result = await swal.fire({
          title: `You have successfully added ${state.form.name}`,
          icon: "success",
          confirmButtonText: "Back Home",
          confirmButtonColor: "#1D99C7",
          showCancelButton: true,
          cancelButtonText: "Share Campaign Order",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        if (result.value) {
          commit("RESET_STATE");
          commit("SET_CREATING", false);
          router.push({ path: "/campaigns" });
        } else {
          commit("SET_CREATING", false);
          swal.close();

          return {
            modal: true,
          };
        }
      }
    } catch (error) {
      commit("SET_CREATING", false);
      return {
        errors: error.response ? error.response.data.error.message : null,
      };
    }
  },
  async update({ state, commit }, { id, bookNow, preview, status }) {
    commit("SET_CREATING", true);

    try {
      // If all campaignImages don't have images set then remove empty campaignImages objects
      if (
        state.form.campaignImages.filter(
          (campaignImage) => campaignImage.image === ""
        ).length
      ) {
        state.form.campaignImages = null;
      }

      let postData = {};

      if (status === "Incomplete" || status === "Draft") {
        postData = { ...state.form, status };
      } else {
        postData = { ...state.form };
      }

      if (state.package.type === "Video") {
        delete postData.logoImage;
        delete postData.contactImage;
      }

      await httpClient.methods.apiPatch(
        `${process.env.VUE_APP_API_URL}/campaigns/${id}`,
        postData
      );

      if (bookNow) {
        commit("RESET_STATE");
        commit("SET_CREATING", false);
        router.push({ path: `/campaigns/${id}/payment` });
      } else if (preview) {
        commit("SET_CREATING", false);
        return {};
      } else {
        const result = await swal.fire({
          title: `You have successfully updated ${state.form.name}`,
          icon: "success",
          confirmButtonText: "Back Home",
          confirmButtonColor: "#1D99C7",
          showCancelButton: true,
          cancelButtonText: "Share Campaign Order",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        commit("SET_CREATING", false);

        if (result.value) {
          commit("RESET_STATE");
          router.push({ path: "/campaigns" });
        } else {
          commit("SET_CREATING", false);
          swal.close();

          return {
            modal: true,
          };
        }
      }
    } catch (error) {
      commit("SET_CREATING", false);
      return {
        errors: error.response ? error.response.data.error.message : null,
      };
    }
  },
  setEditData({ commit }, data) {
    commit("FORMAT_DATA", data);
  },
};
